<template>
  <div class="temp-page">
    <van-loading type="circular" color="#999" />
    <p class="status-message">{{ statusMessage }}</p>
  </div>
</template>

<script>
import { queryArticleVoByTempKey } from "../commJs/articles";

/**
 * 解析稿件 id。
 * @param {Record<string, unknown>} article 稿件
 * @returns {number|null}
 */
function resolveArticleId(article) {
  const raw = article?.id ?? article?.articleId ?? article?.article_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * 解析父直播 id（直播帧场景）。
 * @param {Record<string, unknown>} article 稿件
 * @returns {number|null}
 */
function resolveParentId(article) {
  const raw = article?.parentId ?? article?.parent_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export default {
  name: "Temp",
  props: {
    tempKey: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      statusMessage: "正在加载，请稍候...",
    };
  },
  mounted() {
    this.resolveRedirect();
  },
  methods: {
    goNotFound() {
      this.$router.replace({ name: "index" });
    },
    /**
     * 读取并规范化 from 查询参数。
     * @returns {string}
     */
    resolveFromQuery() {
      const from = this.$route.query.from;
      return typeof from === "string" ? from.trim() : "";
    },
    /**
     * 组装内部页跳转的 query（保留 key，透传 from）。
     * @param {string} tempKey 预览 key
     * @param {Record<string, string>} [extra] 额外 query
     * @returns {Record<string, string>}
     */
    buildRedirectQuery(tempKey, extra) {
      const from = this.resolveFromQuery();
      const query = Object.assign({ key: tempKey }, extra || {});
      if (from) {
        query.from = from;
      }
      return query;
    },
    async resolveRedirect() {
      const tempKey = (this.tempKey || this.$route.params.key || "").trim();
      if (!tempKey) {
        this.goNotFound();
        return;
      }

      try {
        const article = await queryArticleVoByTempKey(tempKey);

        if (!article || typeof article !== "object") {
          this.statusMessage = "找不到内容，即将跳转到首页...";
          setTimeout(() => this.goNotFound(), 1500);
          return;
        }

        const type = Number(article.type ?? 1);
        const id = resolveArticleId(article);
        const from = this.resolveFromQuery();
        const isFromWechat = from.toLowerCase() === "wechat";

        // type 5: 外部链接
        if (type === 5) {
          const externalLink = String(
            article.external_link ?? article.externalLink ?? "",
          ).trim();
          if (externalLink) {
            this.statusMessage = "即将跳转到外部链接...";
            window.location.replace(externalLink);
          } else {
            this.statusMessage = "外部链接无效，即将跳转到首页...";
            setTimeout(() => this.goNotFound(), 1500);
          }
          return;
        }

        let routeName = "news";
        let routeId = id;
        let query = this.buildRedirectQuery(tempKey);

        if (type === 2) {
          routeName = "topic";
        } else if (type === 3) {
          routeName = "live";
        } else if (type === 4 && isFromWechat) {
          // 直播帧：落地到父直播，并把当前帧 id 带给 Live
          const parentId = resolveParentId(article);
          if (!parentId) {
            this.statusMessage = "找不到父直播，即将跳转到首页...";
            setTimeout(() => this.goNotFound(), 1500);
            return;
          }
          if (!id) {
            this.statusMessage = "找不到内容，即将跳转到首页...";
            setTimeout(() => this.goNotFound(), 1500);
            return;
          }
          routeName = "live";
          routeId = parentId;
          query = this.buildRedirectQuery(tempKey, { id: String(id) });
        } else {
          routeName = "news";
        }

        if (!routeId) {
          this.statusMessage = "找不到内容，即将跳转到首页...";
          setTimeout(() => this.goNotFound(), 1500);
          return;
        }

        this.statusMessage = "正在跳转...";
        await this.$router.replace({
          name: routeName,
          params: { id: String(routeId) },
          query,
        });
      } catch (e) {
        console.error("处理临时链接失败:", e);
        this.statusMessage = "加载失败，即将跳转到首页...";
        setTimeout(() => this.goNotFound(), 1500);
      }
    },
  },
};
</script>

<style scoped>
.temp-page {
  text-align: center;
  padding: 50px 20px;
}

.status-message {
  margin-top: 16px;
  color: #666;
  font-size: 14px;
}
</style>
